import { isArray, isObject, isFunction, isPrimitive } from './utils';

const ProxyInternalValueKey = '@@proxy_internal_value';
const ProxyOptionalEmptyValueSymbol = Symbol();

const ProxyEmptyOptionalValue = { [ProxyOptionalEmptyValueSymbol]: true };

export type WrappedValueOpts = {
  interpolate: boolean;
  asHtml?: boolean;
};

export type OptionalKey = Symbol;

export type Methods<V> = {
  isHtml: () => boolean;
  toPath: (relative?: string) => string;
  toJSON: () => V | string;
  toValue: () => V;
  getOpts: () => WrappedValueOpts;
  isTruthy: () => boolean;
  optional: (key: string) => Wrapped<OptionalKey>;
};

type Primitive = string | number | symbol | bigint | boolean;

type Wrapped<V> = V extends Primitive
  ? V & Methods<V>
  : V extends Array<infer U> & Methods<V>
    ? Array<Wrapped<U>>
    : { [P in keyof V]: Wrapped<V[P]> } & Methods<V>;

const factory = <V>(
  path: string,
  value: V,
  opts: WrappedValueOpts = { interpolate: false, asHtml: false },
): Wrapped<V> => buildProxy(buildWrappedValue(path, value, opts));

// todo: make exhaustive of 'Methods' keys + primitive
const targetProps = [
  'isHtml',
  'toPath',
  'toJSON',
  'toValue',
  'getOpts',
  'isTruthy',
  Symbol.toPrimitive,
];

const proxyHandler: ProxyHandler<any> = {
  get(target: Wrapped<any>, prop: string, receiver: any) {
    const trunk = target[ProxyInternalValueKey];
    const inTrunk = trunk.hasOwnProperty(prop);

    if (isFunction(trunk[prop])) {
      return trunk[prop].bind(trunk);
    }

    if (inTrunk) return trunk[prop];

    if (targetProps.includes(<string>prop)) {
      return Reflect.get(target, <string>prop, receiver);
    }

    return target.optional(prop);
  },
};

const buildProxy = (wrapped: any) => new Proxy(wrapped, proxyHandler);

const buildWrappedValue = <V>(path: string, value: V, opts: WrappedValueOpts) => {
  const wrapped: Wrapped<V> =
    isPrimitive(value) || isFunction(value)
      ? value
      : isArray(value)
        ? wrapArray(path, value, opts)
        : isObject(value)
          ? wrapObject(path, value, opts)
          : throwUnsupportedValue(value);

  const methods = buildWrappedValueMethods(path, value, opts);

  return {
    [ProxyInternalValueKey]: wrapped,
    ...methods,
  };
};

const buildWrappedValueMethods = <V>(
  path: string,
  value: V,
  opts: WrappedValueOpts,
): Methods<V> => ({
  isTruthy() {
    return value === ProxyEmptyOptionalValue ? false : !!value;
  },
  optional(key: string): Wrapped<any> {
    return factory<any>(`${path}.${key}`, ProxyEmptyOptionalValue, opts);
  },
  toPath(from = '') {
    return path.replace(from, '');
  },
  isHtml() {
    return opts.asHtml || path.endsWith('_html');
  },
  toJSON() {
    if (opts.interpolate) {
      return value;
    }

    return this.isHtml() ? `{{{ ${this.toPath()} }}}` : `{{ ${this.toPath()} }}`;
  },
  getOpts() {
    return opts;
  },
  toValue() {
    return value;
  },
});

const wrapArray = (path: string, value: any, opts: WrappedValueOpts) =>
  value.map((v: any, index: number) => factory(`${path}[${index}]`, v, opts));

const wrapObject = (path: string, value: any, opts: WrappedValueOpts) =>
  Object.fromEntries(
    Object.entries(value).map(([key, value]) => [
      key,
      factory(path === '' ? key : `${path}.${key}`, value, opts),
    ]),
  );

const throwUnsupportedValue = (value: any) => {
  throw new Error(`Unsupported type ${typeof value}`);
};

export type { Wrapped as WrappedValue };
export { factory as wrapValue };
